import { Component, Input, OnInit, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { finalize, takeUntil, tap } from 'rxjs/operators';

import { PageNames } from 'src/app/configs/page-names.enum';
import { GetEnumValuesDto, OtpStatus } from 'src/app/core/api/models';
import { EventTypes } from 'src/app/core/constants/event-types';
import { BaseIndexModule } from 'src/app/pages/shared/base/modules/base-index.module';
import { PaginatedListOfGetFilterWordsWithPaginationQueryDto } from '../../../../core/api/models/paginated-list-of-get-filter-words-with-pagination-query-dto';
import { PaginatedListOfUserSafeIPsWithPaginationDto } from '../../../../core/api/models/paginated-list-of-user-safe-i-ps-with-pagination-dto';
import { UserSafeIPsWithPaginationDto } from '../../../../core/api/models/user-safe-i-ps-with-pagination-dto';
import { UserSafeIPsService } from '../../../../core/api/services/user-safe-i-ps.service';
import { IpDisplayerPipe } from '../../../../core/pipes/ip-displayer.pipe';
import { AppDomService } from '../../../../core/services/dom.service';
import { BaseIndexPageComponent } from '../../../shared/base/components/base-index-page.component';
import { ConfirmUserSafeIpsFormComponent } from '../confirm-user-safe-ips/confirm-user-safe-ips-form/confirm-user-safe-ips-form.component';
import { SafeIpsFormComponent } from '../safe-ips/safe-ips-form/safe-ips-form.component';

@Component({
    selector: 'app-base-safe-ips-index-page',
    templateUrl: './base-safe-ips-index-page.component.html',
    providers: [IpDisplayerPipe],
    imports: [
        BaseIndexModule,
    ]
})
export class BaseSafeIpsIndexPageComponent
  extends BaseIndexPageComponent
  implements OnInit
{
  @Input() getTableDataApiMethodName;
  @Input() pageTitle;

  //#region Properties

  pageName = PageNames.safeIps;
  protected currentService = inject(UserSafeIPsService);

  createGetApiMethodName: string =
    this.currentService.userSafeIPsCreateGet.name;
  auditApiMethodName: string =
    this.currentService.userSafeIPsGetUserSafeIpAudits.name;

  modelWithPagination$: Observable<PaginatedListOfUserSafeIPsWithPaginationDto>;
  items$: Observable<Array<UserSafeIPsWithPaginationDto>>;

  OtpStatusEnumValues: GetEnumValuesDto[] = [];

  get OtpStatus() {
    return OtpStatus;
  }
  //#endregion

  constructor(private IpDisplayerPipe: IpDisplayerPipe) {
    super();
  }
  ngOnInit() {
    super.ngOnInit();
    this.pageConfig.title = this.pageTitle;
    if (this.pageId == 'confirm-user-safe-ips')
      this.pageConfig.showAddBtn = false;
  }

  getEnumsValues() {
    this.getEnumValuesApi('OtpStatus')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((values) => (this.OtpStatusEnumValues = values));
  }

  configTableColumns() {
    this.tableColumns = [
      { title: 'common.ipAddress', name: 'ipAddress', hasSearch: true },
      {
        title: this.localizer.organizationName,
        name: 'organizationName',
        hasSearch: true,
      },
      {
        title: 'common.useToLoginPanel',
        name: 'useToLoginPanel',
        type: 'boolean',
        hasSearch: true,
      },
      {
        title: 'common.useToCreateToken',
        name: 'useToCreateToken',
        type: 'boolean',
        hasSearch: true,
      },
      {
        title: 'common.status',
        name: 'status',
        hasSearch: true,
        type: 'enum',
        enumName: 'OtpStatus',
      },
      {
        title: 'common.description',
        name: 'description',
        type: 'text',
        hasSearch: true,
      },
      {
        title: this.localizer.createDate,
        name: 'created',
        hasSearch: true,
        type: 'dateTime',
      },
    ];

    if (this.pageId == 'confirm-user-safe-ips')
      this.tableColumns = this.tableColumns.filter(
        (column) => column.name !== 'status'
      );
  }

  //#region Api

  //Get All
  getPaginationApi(): Observable<PaginatedListOfGetFilterWordsWithPaginationQueryDto> {
    return this.currentService[this.getTableDataApiMethodName]({
      body: this.getBaseModelWithPaginationParams(),
    });
  }

  //Delete
  baseDeleteApi(id: number) {
    this.unSubs();

    this.pageConfig.loading = true;
    this.lastSelectedItemId = id;

    this.currentService
      .userSafeIPsDelete({ body: { id: id } })
      .pipe(this.basePipeOperators())
      .subscribe(this.baseDeleteObserver);
  }

  //#endregion

  //#region call Actions

  openCreateFormModal() {
    this.unSubs();

    let data = {
      title: `${this.formPageConfig?.vocabs}addTitle`,
      isEdit: false,
    };
    const modalResult = this.dialogService.openModal({
      component: SafeIpsFormComponent,
      data: data,
    });

    modalResult
      .afterClosed()
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => (this.pageConfig.isVisibleForm = undefined)),
        tap(() => {
          AppDomService.removeModalScrollClassFromHtml();
        })
      )
      .subscribe((res) => {
        if (res) {
          this.getTableData();
          this.cd.detectChanges();
        }
      });
  }

  onUpdate(model: UserSafeIPsWithPaginationDto) {
    this.unSubs();

    let data = {
      title: `${this.formPageConfig?.vocabs}editTitle`,
      model: { id: model.id, title: model.ipAddress },
      isEdit: true,
    };

    const modalResult = this.dialogService.openModal({
      component: SafeIpsFormComponent,
      data: data,
    });

    modalResult
      .afterClosed()
      .pipe(
        takeUntil(this.onDestroy$),
        tap(() => {
          AppDomService.removeModalScrollClassFromHtml();
        })
      )
      .subscribe((res) => {
        if (res) {
          this.getTableData();
          this.cd.detectChanges();
        }
      });
  }

  onReject(id) {
    this.unSubs();
    this.pageConfig.loading = true;

    this.currentService
      .userSafeIPsRejectUserSafeIp({
        body: {
          id: id,
        },
      })
      .pipe(this.basePipeOperators())
      .subscribe(this.baseObserver);
  }

  onConfirm(id: number, useToCreateToken: boolean, useToLoginPanel: boolean) {
    this.unSubs();

    let data = {
      id: id,
      useToLoginPanel: useToLoginPanel,
      useToCreateToken: useToCreateToken,
    };

    const modalResult = this.dialogService.openModal({
      component: ConfirmUserSafeIpsFormComponent,
      data: data,
    });

    modalResult
      .afterClosed()
      .pipe(
        takeUntil(this.onDestroy$),
        tap(() => {
          AppDomService.removeModalScrollClassFromHtml();
        })
      )
      .subscribe((res) => {
        if (res) {
          this.getTableData();
          this.cd.detectChanges();
        }
      });
  }

  openRejectModal(id, ip?: string) {
    const dialogResult = this.getConfirmDialog({
      title: 'messages.rejectUserSafeIp',
      content: ip,
      isConfirmDelete: false,
      confirmBtnColor: 'btn-danger',
      wordBreak: false,
    });

    dialogResult
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result) {
          this.onReject(id);
        }
      });
  }

  onDelete(id, content) {
    try {
      content = this.IpDisplayerPipe.transform(content);
      super.onDelete(id, content);
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.systemManagement
      );
    }
  }

  //#endregion
}
